/**
 * Shared salespage top-nav.
 *
 * Used on the salespage itself AND on every sales-context child route
 * (`/recipes`, `/recipes/:slug`) so the visitor always has a clear way
 * back to the salespage home — never the tenant restaurant.
 *
 * The `?sales=1` query string is stamped on every cross-page link so the
 * RootRouter / RecipesRouter never accidentally render the tenant HomePage
 * when the visitor expected the marketing site.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import HustlePlateLogo from "./HustlePlateLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { isStandalone } from "@/lib/pwa";

/* Calendly + live demo links — keep these in sync with SalesPage.jsx. */
const CFG = (typeof window !== "undefined" && window.__SALES_CONFIG__) || {};
const CALENDAR_URL = CFG.CALENDAR_URL || "https://calendly.com/hustleplate/demo";
const DEMO_URL     = CFG.DEMO_URL     || "/?biz=demo-trattoria";

/** Build a hash link that ALWAYS resolves to the salespage, no matter
 *  which sub-route the visitor is on. On `/` we just use the bare hash so
 *  the browser scrolls in-place; everywhere else we hop back to `/?sales=1`
 *  and let the anchor work after RootRouter renders the salespage. */
function buildHashLink(currentPath, anchor) {
  if (currentPath === "/" || currentPath === "") {
    return `#${anchor}`;
  }
  return `/?sales=1#${anchor}`;
}

export default function SalesNav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`sp-nav${scrolled ? " sp-nav--scrolled" : ""}`}
      data-testid="sales-nav"
    >
      <div className="sp-container sp-nav__inner">
        {/* Logo is an explicit Link to /?sales=1 so we ALWAYS land on the
            salespage home — never on a tenant restaurant. */}
        <Link
          to="/?sales=1"
          className="sp-nav__logo-link"
          aria-label="HustlePlate home"
          data-testid="sales-nav-logo"
        >
          <HustlePlateLogo size={36} />
        </Link>
        <div className="sp-nav__links">
          <a href={buildHashLink(pathname, "features")} data-testid="sales-nav-features">
            Features
          </a>
          <a href={buildHashLink(pathname, "menu-demo")} data-testid="sales-nav-menu">
            Menu demo
          </a>
          <a href={buildHashLink(pathname, "compare")}>Compare</a>
          {!isStandalone() && (
            <a href={buildHashLink(pathname, "pricing")}>Pricing</a>
          )}
          <Link to="/recipes?sales=1" data-testid="sales-nav-recipes">
            Recipes
          </Link>
          <a href={buildHashLink(pathname, "faq")}>FAQ</a>
          <a href={DEMO_URL} target="_blank" rel="noreferrer" data-testid="sales-nav-demo">
            Live Demo
          </a>
        </div>
        <a
          href={CALENDAR_URL}
          target="_blank"
          rel="noreferrer"
          className="sp-btn sp-btn--primary"
          data-testid="sales-nav-cta"
        >
          Book a demo
        </a>
        <ThemeToggle testid="sales-nav-theme" className="ml-2" />
      </div>
    </nav>
  );
}
